/**
 * @module ProviderBriefingCompare
 * @purpose Side-by-side display of both provider briefings for a cluster (FR4/FR8/FR9).
 *   Shows provider/model badges, the 7-key briefing content, and guard provenance where present.
 * @depends types/reasoning, GuardProvenanceBadge, react-i18next
 * @dependedOnBy BriefingTile (expanded compare panel)
 *
 * @rules
 * - Performance: pure display — all data passed via props
 * - State: none
 * - Errors: n/a — purely presentational
 */

import { useTranslation } from 'react-i18next'
import { GuardProvenanceBadge } from './GuardProvenanceBadge'
import type { ProviderBriefing, AllProviderBriefings } from '../../../../../types/reasoning'

interface ProviderBriefingCompareProps {
  allBriefings: AllProviderBriefings
}

const PANEL: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: '8px',
  padding: '12px',
  background: 'var(--bg-card)',
  minWidth: 0,
  flex: '1 1 300px',
}

const BADGE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.62rem',
  padding: '2px 8px',
  borderRadius: '4px',
  background: 'var(--accent)',
  color: 'var(--btn-primary-text, #fff)',
  display: 'inline-block',
  marginBottom: '8px',
}

const LABEL_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.65rem',
  color: 'var(--text-dim)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '2px',
}

const HEADLINE_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: '0.88rem',
  color: 'var(--text)',
  fontWeight: 600,
  marginBottom: '6px',
}

const BODY_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.78rem',
  color: 'var(--text-dim)',
  lineHeight: 1.5,
  marginBottom: '8px',
}

function BriefingPanel({ briefing: pb }: { briefing: ProviderBriefing }): React.ReactElement {
  const { t } = useTranslation('analysis')
  const labelKey = pb.providerLabel === 'primary'
    ? 'reasoning.briefingBoard.compare.primaryLabel'
    : 'reasoning.briefingBoard.compare.alternateLabel'

  return (
    <div style={PANEL}>
      <div style={BADGE}>
        {t(labelKey)} — {pb.provider} / {pb.model}
      </div>
      <div style={HEADLINE_STYLE}>{pb.briefing.headline}</div>
      <p style={BODY_STYLE}>{pb.briefing.summary}</p>
      {pb.briefing.supported_points.length > 0 && (
        <div>
          <div style={LABEL_STYLE}>{t('reasoning.briefingBoard.summary.supportedPointsLabel')}</div>
          <ul style={{ margin: 0, paddingLeft: '16px' }}>
            {pb.briefing.supported_points.map((point) => (
              <li key={point} style={BODY_STYLE}>{point}</li>
            ))}
          </ul>
        </div>
      )}
      {pb.guardProvenance !== null && (
        <GuardProvenanceBadge provenance={pb.guardProvenance} />
      )}
    </div>
  )
}

/**
 * Side-by-side provider briefing panels with provider/model badges and guard provenance (FR4/FR8/FR9).
 *
 * @behaviour Renders one panel per cached briefing. Empty state shown when no briefings exist.
 *   guardProvenance section rendered only when non-null (pre-guard rows omit it).
 * @errors n/a — purely presentational
 */
export function ProviderBriefingCompare({
  allBriefings,
}: ProviderBriefingCompareProps): React.ReactElement {
  const { t } = useTranslation('analysis')

  if (allBriefings.briefings.length === 0) {
    return (
      <p style={BODY_STYLE}>
        {t('reasoning.briefingBoard.compare.noBriefings')}
      </p>
    )
  }

  return (
    <div
      style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '8px' }}
      aria-label={t('reasoning.briefingBoard.compare.title')}
    >
      {allBriefings.briefings.map((pb) => (
        <BriefingPanel key={pb.providerLabel} briefing={pb} />
      ))}
    </div>
  )
}
